import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const NAV = [
  { id: "dashboard",    icon: "ti-layout-dashboard", label: "Dashboard",    path: "/dashboard" },
  { id: "leads",        icon: "ti-users",             label: "Leads",        path: "/dashboard/leads" },
  { id: "inbox",        icon: "ti-message",           label: "Inbox",        path: "/dashboard/inbox" },
  { id: "appointments", icon: "ti-calendar",          label: "Appointments", path: "/dashboard/appointments" },
  { id: "policies",     icon: "ti-file-description",  label: "Policies",     path: "/dashboard/policies" },
  { id: "analytics",   icon: "ti-chart-bar",          label: "Analytics",    path: "/dashboard/analytics" },
  { id: "scrape",       icon: "ti-world-search",      label: "Lead Scraper", path: "/dashboard/scrape", badge: "PRO" },
  { id: "workflows",    icon: "ti-git-branch",        label: "Workflows",    path: "/dashboard/workflows" },
  { id: "licenses",     icon: "ti-map-pin",           label: "Licenses",     path: "/dashboard/licenses" },
  { id: "estimator",    icon: "ti-calculator",        label: "Price Estimator", path: "/dashboard/estimator" },
  { id: "budget",       icon: "ti-chart-pie",         label: "Budget Tracker",  path: "/dashboard/budget" },
  { id: "cpa",          icon: "ti-chart-line",         label: "CPA Tracker",      path: "/dashboard/cpa" },
  { id: "settings",     icon: "ti-settings",          label: "Settings",     path: "/dashboard/settings" },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const [open, setOpen] = useState(true);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{
      width: open ? 220 : 60,
      flexShrink: 0,
      height: '100vh',
      background: 'var(--bg2)',
      borderRight: '0.5px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Logo */}
      <div style={{ padding: '16px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '0.5px solid var(--border)', flexShrink: 0, minHeight: 56 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 12px rgba(127,119,221,0.35)' }}>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: '-0.5px' }}>F</span>
        </div>
        {open && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.8px', color: 'var(--text)', lineHeight: 1 }}>FLO</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{user?.plan}</div>
          </div>
        )}
        <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'all 0.15s' }} onMouseEnter={e => e.currentTarget.style.color='var(--text)'} onMouseLeave={e => e.currentTarget.style.color='var(--text3)'}>
          <i className={`ti ${open ? 'ti-layout-sidebar-left-collapse' : 'ti-layout-sidebar-left-expand'}`} style={{ fontSize: 16 }} aria-hidden="true" />
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'visible' }}>
        {NAV.map(n => {
          const active = isActive(n.path);
          return (
            <button key={n.id} onClick={() => navigate(n.path)} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: open ? '8px 10px' : '8px',
              justifyContent: open ? 'flex-start' : 'center',
              borderRadius: 8,
              border: active
                ? '1px solid rgba(83, 74, 183, 0.6)'
                : n.premium
                  ? '1px solid rgba(127, 119, 221, 0.2)'
                  : '1px solid transparent',
              outline: active ? '1px solid rgba(83, 74, 183, 0.6)' : 'none',
              outlineOffset: '0px',
              boxShadow: active
                ? '0 0 12px rgba(83, 74, 183, 0.4), 0 0 24px rgba(83, 74, 183, 0.2)'
                : 'none',
              cursor: 'pointer',
              background: active ? 'var(--purple-light)' : n.premium ? 'rgba(127,119,221,0.05)' : 'transparent',
              fontWeight: active ? 600 : 500,
              fontSize: 13,
              textAlign: 'left',
              whiteSpace: 'nowrap',
              width: '100%',
              overflow: 'hidden',
              fontFamily: 'var(--font)',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              if (!active) {
                e.currentTarget.style.background = 'var(--bg3)';
                e.currentTarget.style.boxShadow = '0 0 8px rgba(83, 74, 183, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(83, 74, 183, 0.3)';
                e.currentTarget.style.outline = '1px solid rgba(83, 74, 183, 0.3)';
                e.currentTarget.style.outlineOffset = '0px';
              }
            }}
            onMouseLeave={e => {
              if (!active) {
                e.currentTarget.style.background = n.premium ? 'rgba(127,119,221,0.05)' : 'transparent';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = n.premium ? 'rgba(127, 119, 221, 0.2)' : 'transparent';
                e.currentTarget.style.outline = 'none';
              }
            }}
            >
              <i className={`ti ${n.icon}`} style={{ fontSize: 17, color: active ? '#534AB7' : n.premium ? '#7F77DD' : 'var(--text2)', flexShrink: 0 }} aria-hidden="true" />
              {open && (
                <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                  color: active ? '#534AB7' : 'var(--text2)',
                  ...(n.premium && !active ? { background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 700 } : {}),
                }}>
                  {n.label}
                  {n.badge && (
                    <span style={{ background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, letterSpacing: '0.4px', flexShrink: 0, WebkitBackgroundClip: 'unset', WebkitTextFillColor: 'unset' }}>
                      {n.badge}
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding: '10px 8px', borderTop: '0.5px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        <button onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: open ? '7px 10px' : '7px', justifyContent: open ? 'flex-start' : 'center', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text2)', fontSize: 13, fontFamily: 'var(--font)', width: '100%', fontWeight: 500, transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
          <i className={`ti ${dark ? 'ti-sun' : 'ti-moon'}`} style={{ fontSize: 17, flexShrink: 0 }} aria-hidden="true" />
          {open && <span>{dark ? 'Light mode' : 'Dark mode'}</span>}
        </button>

        {open && user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--bg3)', marginTop: 2 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>{(user.name || 'U')[0].toUpperCase()}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
            </div>
            <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, fontSize: 14, borderRadius: 4, flexShrink: 0, transition: 'all 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color='var(--text)'}
              onMouseLeave={e => e.currentTarget.style.color='var(--text3)'}
              title="Sign out">
              <i className="ti ti-logout" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
